import React from 'react';
import DocLayout from '../DocLayout';
import { Settings, Building2 } from 'lucide-react';

const WorkspaceSetup = () => {
  return (
    <DocLayout title="Setting Up Your Workspace" category="Getting Started">
      <div className="space-y-6">
        <section>
          <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Settings className="w-6 h-6 text-indigo-600" />
            Workspace Configuration
          </h2>
          <p className="text-slate-700 mb-4">
            Configure your I-Match workspace to match your business needs and workflow.
          </p>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Company Profile Setup</h3>
          <p className="text-slate-700 mb-3">
            Navigate to <strong>Admin Panel → Company Profile</strong> to configure:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-slate-700">
            <li><strong>Company Information</strong>: Update name, description, and contact details</li>
            <li><strong>Services Offered</strong>: Define what services your company provides</li>
            <li><strong>Products</strong>: List your product offerings</li>
            <li><strong>Industries Served</strong>: Specify your target markets</li>
            <li><strong>Geographic Focus</strong>: Define your operational regions</li>
            <li><strong>Unique Value Propositions</strong>: Highlight what makes you different</li>
            <li><strong>Compliance & Certifications</strong>: Add relevant badges (SOC 2, GDPR, ISO)</li>
          </ul>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Lead Pipeline Stages</h3>
          <p className="text-slate-700 mb-3">
            I-Match comes with standard pipeline stages:
          </p>
          <ol className="list-decimal pl-6 space-y-2 text-slate-700">
            <li><strong>New</strong>: Freshly imported or created leads</li>
            <li><strong>Contacted</strong>: Initial outreach completed</li>
            <li><strong>Qualified</strong>: Lead meets your criteria</li>
            <li><strong>Proposal</strong>: Proposal sent to lead</li>
            <li><strong>Negotiation</strong>: In final negotiation phase</li>
            <li><strong>Won</strong>: Successfully closed deal</li>
            <li><strong>Lost</strong>: Opportunity lost</li>
          </ol>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">User Roles & Permissions</h3>
          <p className="text-slate-700 mb-3">
            I-Match supports two main user roles:
          </p>
          <div className="space-y-3">
            <div className="border rounded-lg p-4">
              <h4 className="font-semibold text-slate-900 mb-2">Admin Users</h4>
              <ul className="list-disc pl-6 space-y-1 text-slate-700 text-sm">
                <li>Full access to all CRM features</li>
                <li>User management capabilities</li>
                <li>Access to admin panel</li>
                <li>Company profile management</li>
                <li>Usage tracking and analytics</li>
              </ul>
            </div>
            <div className="border rounded-lg p-4">
              <h4 className="font-semibold text-slate-900 mb-2">Normal Users</h4>
              <ul className="list-disc pl-6 space-y-1 text-slate-700 text-sm">
                <li>Access to leads, contacts, companies</li>
                <li>Can create and manage tasks</li>
                <li>Calendar and activity tracking</li>
                <li>Email template usage</li>
                <li>No admin panel access</li>
              </ul>
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Customization Options</h3>
          <ul className="list-disc pl-6 space-y-2 text-slate-700">
            <li><strong>Email Templates</strong>: Create reusable email templates for common scenarios</li>
            <li><strong>Notification Preferences</strong>: Configure how you receive alerts</li>
            <li><strong>Dashboard Layout</strong>: Customize your view preferences</li>
            <li><strong>Export Settings</strong>: Configure data export permissions</li>
          </ul>
        </section>

        <section>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-slate-900 mb-2">💡 Pro Tip</h4>
            <p className="text-slate-700 text-sm">
              Spend time setting up your company profile completely. This information is used by AI features to provide better lead enrichment and personalized email generation.
            </p>
          </div>
        </section>
      </div>
    </DocLayout>
  );
};

export default WorkspaceSetup;